/**
 * The modal's Metric Filters panel: the integration / metric path, the
 * Property (the client's connected accounts for this source), a Secondary
 * Dimension, and one "<Dimension> is Any" row per dimension. A row opens a
 * searchable list of the values this client actually has; ticking one
 * filters the preview at once.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { reportsApi, type CatalogueSource, type Filter } from '../reports/reports-api.ts';

const VALUE_LIMIT = 100;

function useValues(props: {
  organisationId: string;
  clientId: string;
  timezone: string;
  source: string;
  metric: string;
  dimension: string | null;
}): string[] | null {
  const [values, setValues] = useState<string[] | null>(null);
  const { organisationId, clientId, timezone, source, metric, dimension } = props;
  useEffect(() => {
    if (!dimension) return undefined;
    let cancelled = false;
    setValues(null);
    reportsApi
      .query(organisationId, {
        business_unit_id: clientId,
        mode: 'LIVE',
        timezone,
        range: { preset: 'LAST_N_MONTHS', n: 3 },
        widgets: [{ id: 'values', source, metrics: [metric], dimension, limit: VALUE_LIMIT }],
      })
      .then(
        ({ results }) => !cancelled && setValues((results[0]?.rows ?? []).map((row) => row.label)),
      )
      .catch(() => !cancelled && setValues([]));
    return () => {
      cancelled = true;
    };
  }, [organisationId, clientId, timezone, source, metric, dimension]);
  return values;
}

function summary(filter: Filter | undefined): string {
  if (!filter || filter.values.length === 0) return 'Any';
  if (filter.values.length === 1) return filter.values[0] ?? 'Any';
  return `${filter.values.length} values`;
}

export function KpiFilterPanel(props: {
  organisationId: string;
  clientId: string;
  timezone: string;
  source: CatalogueSource;
  metric: string;
  properties: string[];
  filters: Filter[];
  secondaryDimension: string | null;
  onFilters: (filters: Filter[]) => void;
  onSecondaryDimension: (dimension: string | null) => void;
}): ReactNode {
  const [openDimension, setOpenDimension] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const values = useValues({ ...props, source: props.source.key, dimension: openDimension });
  const dimensions = props.source.dimensions.filter((item) => item.key !== 'date');
  const metricLabel = props.source.metrics.find((item) => item.key === props.metric)?.label;
  const toggle = (dimension: string, value: string) => {
    const current = props.filters.find((item) => item.dimension === dimension)?.values ?? [];
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    const others = props.filters.filter((item) => item.dimension !== dimension);
    props.onFilters(next.length ? [...others, { dimension, op: 'is', values: next }] : others);
  };

  return (
    <section className="kp-panel" aria-labelledby="kp-filters-title">
      <h3 id="kp-filters-title">Metric Filters</h3>
      <p className="kp-path">
        {props.source.label} / {metricLabel ?? props.metric}
      </p>
      <p className="kp-field-static">
        <span>Property</span>
        <strong>
          {props.properties.length ? props.properties.join(', ') : 'All connected accounts'}
        </strong>
      </p>
      <label className="rs-field">
        Secondary Dimension
        <select
          value={props.secondaryDimension ?? ''}
          onChange={(event) => props.onSecondaryDimension(event.target.value || null)}
        >
          <option value="">None</option>
          {dimensions.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
      <ul className="kp-filter-list">
        {dimensions.map((item) => {
          const filter = props.filters.find((entry) => entry.dimension === item.key);
          const isOpen = openDimension === item.key;
          const term = search.trim().toLowerCase();
          return (
            <li key={item.key}>
              <button
                type="button"
                className={`kp-filter-row${filter ? ' on' : ''}`}
                aria-expanded={isOpen}
                onClick={() => {
                  setSearch('');
                  setOpenDimension(isOpen ? null : item.key);
                }}
              >
                {item.label} is <strong>{summary(filter)}</strong>
              </button>
              {isOpen && (
                <div className="kp-values">
                  <input
                    type="search"
                    placeholder={`Search ${item.label.toLowerCase()}`}
                    aria-label={`Search ${item.label}`}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {values === null && <p className="kp-faint">Loading values…</p>}
                  {values?.length === 0 && (
                    <p className="kp-faint">No values in the last 3 months.</p>
                  )}
                  {values
                    ?.filter((value) => !term || value.toLowerCase().includes(term))
                    .map((value) => (
                      <label key={value} className="kp-check">
                        <input
                          type="checkbox"
                          checked={filter?.values.includes(value) ?? false}
                          onChange={() => toggle(item.key, value)}
                        />
                        {value}
                      </label>
                    ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
